import {Injectable, signal} from '@angular/core';
import {SocketIoFactory} from './socket-io-factory.service';
import {EmployeeService} from './employee.service';
import {EmployeeSyncStatus} from '../models/employee.model';
import {environment} from '../../environments/environment';
import {ResilientSocketConnection} from './resilient-socket-connection';

/**
 * Hält den Status des vollen Mitarbeiter-Abgleichs (siehe Backend ProcessEmployeeFullSync)
 * aktuell - per WebSocket-Push vom Backend (EmployeeSyncGateway, /ws/employee-sync) statt per
 * festem Polling-Budget: der reale Jobabschluss kann durch zwei nacheinander laufende
 * Backend-Cronjobs (Status-Polling, dann Ergebnis-Auslesen) bis zu ~2 Minuten dauern - ein
 * Live-Test zeigte, dass ein festes 30s-Polling-Fenster dabei reproduzierbar hängen blieb.
 *
 * Fällt bei WebSocket-Ausfall auf Polling gegen GET /employees/sync-status zurück (analog zu
 * AuthStatusService, siehe die gemeinsame ResilientSocketConnection-Basisklasse), damit die
 * Anzeige auch ohne funktionierende Live-Verbindung nicht dauerhaft hängen bleibt.
 */
@Injectable({
    providedIn: 'root'
})
export class EmployeeSyncStatusService extends ResilientSocketConnection<EmployeeSyncStatus> {
    private status = signal<EmployeeSyncStatus>({state: null, startedAt: null});
    // zählt jede tatsächlich empfangene Statusaktualisierung (Push oder Poll-Fetch) hoch -
    // unabhängig davon, ob sich der Wert gegenüber dem vorherigen unterscheidet. Ein reiner
    // Wertevergleich reicht nicht: zwei aufeinanderfolgende Syncs enden beide typischerweise mit
    // demselben state ("COMPLETED"), nur startedAt unterscheidet sich - Aufrufer, die exakt
    // wissen müssen "kam seit Zeitpunkt X eine NEUE Aktualisierung an", brauchen diesen Zähler
    // statt employeeSyncStatus() selbst zu vergleichen (siehe EmployeeTableComponent).
    private revision = signal<number>(0);
    private readonly POLL_INTERVAL_MS = 10000;

    constructor(private employeeService: EmployeeService, socketFactory: SocketIoFactory) {
        super(socketFactory);
        this.fetchStatusOnce();
        this.initConnection();
    }

    getStatus() {
        return this.status.asReadonly();
    }

    getRevision() {
        return this.revision.asReadonly();
    }

    protected getWsUrl(): string {
        if (environment.employeeSyncWsUrl) {
            return environment.employeeSyncWsUrl;
        }
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}/ws/employee-sync`;
    }

    protected getEventName(): string {
        return 'sync-status';
    }

    protected handleEvent(data: EmployeeSyncStatus): void {
        this.setStatus(data);
    }

    protected getPollIntervalMs(): number {
        return this.POLL_INTERVAL_MS;
    }

    protected poll(): void {
        this.fetchStatusOnce();
    }

    private setStatus(status: EmployeeSyncStatus) {
        this.status.set(status);
        this.revision.update(r => r + 1);
    }

    private fetchStatusOnce() {
        this.employeeService.getSyncStatus().subscribe({
            next: (status) => this.setStatus(status)
        });
    }
}
